import React, { useMemo, useState, useEffect, useLayoutEffect, useCallback, } from 'react';
import delay from 'lodash/delay';
import get from 'lodash/get';
import head from 'lodash/head';
import { useSelector, useDispatch } from 'react-redux';
import { getProductsById } from '@yme/api';
import { ExpandLess } from '@yme/assets/icons';
import { Alert } from '@yme/atoms/alert';
import { Button, CONTAINED_VARIANT, ICON_ONLY, NO_ICON, SIZE_44, TEXT_VARIANT, } from '@yme/atoms/button';
import { Row, Container, FlexibleCol } from '@yme/atoms/grid';
import { ANIMATION_TRANSITION_DURATION, MobileDropdownSlider, } from '@yme/atoms/mobile-dropdown-slider';
import { Tooltip, TooltipPlacement } from '@yme/atoms/tooltip';
import { addComparisonProduct, removeAllComparisonProducts, removeComparisonProduct, setInitialComparisonState, } from '@yme/components/Comparison/redux/actions';
import { getProducts } from '@yme/components/Comparison/redux/selectors';
import { setCompareProductsStickyMenuShown } from '@yme/components/Header/redux/actions';
import { useWindowSize } from '@yme/hooks/index';
import { SelectModelModalType } from '@yme/modals/SelectYourModelModal/constants';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { CompareProductCard, CompareEmptyCard, getEmptyCardItems, MAX_COMPARE_PRODUCTS, MIN_COMPARE_PRODUCTS, CompareEmptyCardType, } from '@yme/molecules/cards/compare-product-card';
import { getRTL, getRootLang } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { buildKey } from '@yme/utils/common';
import { getComparisonPageSearchParams } from './helpers';
import { StickyBarStyled, LinkStyled } from './styled';
export function CompareProducts({ comparisonPagePath, endpoints, categoryModel, defaultStartYear, }) {
    const [isOpenMobileSlider, setIsOpenMobileSlider] = useState(false);
    const [isStickyBarExpanded, setIsStickyBarExpanded] = useState(false);
    const dispatch = useDispatch();
    const isRightToLeft = useSelector(getRTL);
    const language = useSelector(getRootLang);
    const compareProducts = useSelector(getProducts);
    const numberProducts = compareProducts.length;
    const isNotEnoughProducts = numberProducts < MIN_COMPARE_PRODUCTS;
    const { isInfiniteDesktopView, isLargeDesktopView, isMobileView } = useWindowSize();
    const { t } = useTranslation();
    useEffect(() => {
        const isCompareProductsShown = numberProducts > 0;
        if (isCompareProductsShown) {
            dispatch(setCompareProductsStickyMenuShown(true));
        }
        else {
            dispatch(setCompareProductsStickyMenuShown(false));
        }
        return () => {
            dispatch(setCompareProductsStickyMenuShown(false));
        };
    }, [dispatch, numberProducts]);
    useEffect(() => {
        if (numberProducts === 0 && isOpenMobileSlider) {
            setIsOpenMobileSlider(false);
        }
    }, [numberProducts, isOpenMobileSlider]);
    useEffect(() => {
        dispatch(setInitialComparisonState());
    }, [dispatch]);
    useLayoutEffect(() => {
        if (isOpenMobileSlider) {
            setIsStickyBarExpanded(isOpenMobileSlider);
        }
        else {
            delay(() => setIsStickyBarExpanded(isOpenMobileSlider), ANIMATION_TRANSITION_DURATION);
        }
    }, [isOpenMobileSlider]);
    const handleRemoveProduct = useCallback((productKey) => {
        dispatch(removeComparisonProduct(productKey));
    }, [dispatch]);
    const handleRemoveAllProducts = useCallback(() => {
        dispatch(removeAllComparisonProducts());
    }, [dispatch]);
    const emptyCartItems = useMemo(() => getEmptyCardItems(MAX_COMPARE_PRODUCTS - compareProducts.length), [compareProducts.length]);
    const productKeys = useMemo(() => compareProducts.map((product) => String(product.key)), [compareProducts]);
    const handleToggleModal = useCallback(() => {
        setIsOpenMobileSlider((prev) => !prev);
    }, []);
    const handleClickLink = useCallback((event) => {
        if (isNotEnoughProducts) {
            event.preventDefault();
        }
    }, [isNotEnoughProducts]);
    const addNewModelCallback = useCallback(async (newProduct) => {
        if (newProduct && endpoints && endpoints?.productsById) {
            getProductsById(`${get(endpoints, 'productsById')}${newProduct.key}`).then((result) => {
                const product = head(result);
                if (product && !compareProducts.some((item) => item.key === product.key)) {
                    dispatch(addComparisonProduct(product));
                }
            });
        }
    }, [compareProducts, dispatch, endpoints]);
    const openAddNewModelModal = useCallback(() => {
        if (categoryModel?.startFromYear &&
            categoryModel?.inRiverId &&
            endpoints?.categoryInfo &&
            endpoints?.productsByQuery &&
            endpoints?.projectInfo &&
            defaultStartYear) {
            dispatch(setIsModalShowed({
                type: MODALS.SELECT_YOUR_MODEL_MODAL,
                data: {
                    type: SelectModelModalType.COMPARISON,
                    title: t('comparison_addModelPopUp_header'),
                    defaultStartYear,
                    primaryButtonContent: t('common_confirm_button'),
                    secondaryButtonContent: t('common_cancel_button'),
                    callback: addNewModelCallback,
                    categories: [categoryModel],
                    labels: {
                        searchInputNoResultsHeader: t('selectModel_searchInputNoResults_header'),
                        searchInputNoResultsDescription: t('selectModel_searchInputNoResults_description'),
                        selectYearLabel: t('comparison_addModelPopUpSelectYear_label'),
                        selectYearPlaceholder: t('comparison_addModelPopUpSelectYear_placeholder'),
                        selectModelLabel: t('comparison_addModelPopUpSelectModel_label'),
                        selectModelPlaceholder: t('comparison_addModelPopUpSelectModel_placeholder'),
                    },
                    endpoints: {
                        categoryInfo: get(endpoints, 'categoryInfo', ''),
                        productsByQuery: get(endpoints, 'productsByQuery', ''),
                        projectInfo: get(endpoints, 'projectInfo', ''),
                        comparingProductsByQuery: get(endpoints, 'comparingProductsByQuery', ''),
                    },
                },
            }));
        }
    }, [addNewModelCallback, categoryModel, defaultStartYear, dispatch, endpoints, t]);
    const MobileSliderFooter = (React.createElement(FlexWrapper, { padding: "16px" },
        React.createElement(Button, { buttonVariant: CONTAINED_VARIANT, hasIconButton: NO_ICON, isRightToLeft: isRightToLeft, label: t('productsGrid_compare_button'), size: SIZE_44 })));
    return (React.createElement(React.Fragment, null,
        React.createElement(StickyBarStyled, { isHidden: numberProducts === 0, isStickyBarExpanded: isStickyBarExpanded },
            React.createElement(Container, { isRightToLeft: isRightToLeft },
                React.createElement(Row, null,
                    isInfiniteDesktopView && (React.createElement(FlexibleCol, { lg: 9 },
                        React.createElement(FlexWrapper, { gap: isLargeDesktopView ? '16px' : '12px', isFullWidth: true, alignItems: "center", height: "100%" },
                            compareProducts.map((item) => (React.createElement(CompareProductCard, { key: item.id, productKey: String(item.key), image: item.image?.url, title: item.title, price: item.price, isShowImage: isLargeDesktopView, language: language, hasTooltipTitle: isInfiniteDesktopView, handleRemoveProduct: handleRemoveProduct }))),
                            emptyCartItems.map((_, index) => (React.createElement(CompareEmptyCard, { onClick: openAddNewModelModal, type: CompareEmptyCardType.STICKY, key: buildKey('empty-card', index), label: t('productsGrid_addModel_button') })))))),
                    React.createElement(FlexibleCol, { lg: 3 },
                        React.createElement(FlexWrapper, { gap: "8px", isFullWidth: true, alignItems: "center", height: "100%" },
                            !isOpenMobileSlider && !isMobileView && (React.createElement(Button, { width: "auto", buttonVariant: TEXT_VARIANT, hasIconButton: NO_ICON, isRightToLeft: isRightToLeft, onClick: handleRemoveAllProducts, label: t('filters_clearAll_label'), size: SIZE_44 })),
                            React.createElement(Tooltip, { placement: TooltipPlacement.ABOVE_RIGHT, title: t('productsGrid_youNeedToSelectProducts_message'), hasEnableHoverListener: isNotEnoughProducts && isInfiniteDesktopView },
                                React.createElement(LinkStyled, { to: {
                                        pathname: comparisonPagePath,
                                        search: getComparisonPageSearchParams(productKeys),
                                    }, onClick: handleClickLink },
                                    React.createElement(Button, { buttonVariant: CONTAINED_VARIANT, hasIconButton: NO_ICON, isRightToLeft: isRightToLeft, label: `${t('productsGrid_compare_button')} (${numberProducts})`, size: SIZE_44, disabled: isNotEnoughProducts }))),
                            !isInfiniteDesktopView && (React.createElement(Button, { buttonVariant: TEXT_VARIANT, hasIconButton: ICON_ONLY, iconComponent: ExpandLess, isRightToLeft: isRightToLeft, onClick: handleToggleModal, size: SIZE_44, iconRotate: isOpenMobileSlider ? 180 : 0 }))))))),
        React.createElement(MobileDropdownSlider, { isActive: isOpenMobileSlider, handleActiveMobileSlider: () => setIsOpenMobileSlider(false), footerComponent: MobileSliderFooter, isRightToLeft: isRightToLeft, isFullScreenHidden: true },
            React.createElement(FlexWrapper, { justifyContent: "center", isFullWidth: true, flexDirection: "column", gap: "10px" },
                compareProducts.map((product) => (React.createElement(CompareProductCard, { isShowImage: true, key: product.id, image: product.image?.url, title: product.title, productKey: String(product.key), price: product.price, language: language, handleRemoveProduct: handleRemoveProduct }))),
                isNotEnoughProducts && (React.createElement(Alert, { message: t('productsGrid_youNeedToSelectProducts_message'), alignCenter: true }))))));
}
